/**
 * Staff Dashboard widget: Arkesel SMS credit. Stays hidden while there is
 * plenty of credit and nothing has failed — speaks up before counseling slips
 * and alerts start bouncing.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { MessageSquareWarning, ArrowRight } from 'lucide-react';
import api from '../lib/api';

const TONES = {
  empty: { border: 'border-rose-300', from: 'from-rose-50', chip: 'bg-rose-100 text-rose-900 border-rose-200', icon: 'bg-rose-100 text-rose-700', btn: 'bg-rose-600 hover:bg-rose-700' },
  low: { border: 'border-amber-300', from: 'from-amber-50', chip: 'bg-amber-100 text-amber-900 border-amber-200', icon: 'bg-amber-100 text-amber-700', btn: 'bg-amber-600 hover:bg-amber-700' },
  error: { border: 'border-rose-300', from: 'from-rose-50', chip: 'bg-rose-100 text-rose-900 border-rose-200', icon: 'bg-rose-100 text-rose-700', btn: 'bg-rose-600 hover:bg-rose-700' },
  unknown: { border: 'border-slate-300', from: 'from-slate-50', chip: 'bg-slate-100 text-slate-900 border-slate-200', icon: 'bg-slate-100 text-slate-700', btn: 'bg-slate-700 hover:bg-slate-800' },
};

export default function SmsCreditWidget() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/sms/credit')
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, []);

  if (!data?.enabled) return null;
  const failed = data.failed_24h || 0;
  const tone = TONES[data.level];
  if (!tone && failed === 0) return null;

  const t = tone || TONES.low;
  const headline = data.level === 'empty' ? 'SMS credit is finished'
    : data.level === 'low' ? 'SMS credit running low'
      : data.level === 'error' ? 'Cannot reach your SMS account'
        : failed > 0 ? 'Some texts are failing' : 'SMS credit unconfirmed';

  const detail = data.level === 'error' ? data.error
    : data.balance === null || data.balance === undefined
      ? 'Arkesel did not report a credit figure.'
      : `${data.balance} SMS credit${data.balance === 1 ? '' : 's'} left — counseling slips and alerts stop once this hits zero.`;

  return (
    <Card
      data-testid="sms-credit-widget"
      className={`${t.border} bg-gradient-to-br ${t.from} to-white shadow-[0_4px_24px_-8px_rgba(217,119,6,0.3)]`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <span className={`h-9 w-9 rounded-xl ${t.icon} grid place-items-center shrink-0`}>
              <MessageSquareWarning className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-800 flex items-center gap-2 flex-wrap">
                {headline}
                {failed > 0 && (
                  <Badge className={`rounded-full ${t.chip} text-[10px]`} data-testid="sms-credit-failed-count">
                    {failed} failed in 24h
                  </Badge>
                )}
              </div>
              <p className="text-xs text-slate-600 mt-0.5" data-testid="sms-credit-detail">{detail}</p>
            </div>
          </div>
          <Button asChild size="sm" className={`rounded-full gap-1.5 ${t.btn}`} data-testid="sms-credit-log-btn">
            <Link to="/sms-log">Open SMS log <ArrowRight className="h-3 w-3" /></Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
